import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  fetchConversationListing,
  fetchConversationThread,
  setSelectedConversationId,
} from 'src/redux/chatbotConversations'
import { formatDate, truncateText, useDebouncedFilter } from './helpers'

const ConversationTable = () => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const { conversations, conversationsLoading, selectedConversationId } = useSelector(
    state => state.chatbotConversations,
  )

  const { filterValue: emailFilter, handleFilterChange: handleEmailFilterChange } =
    useDebouncedFilter(value => {
      dispatch(fetchConversationListing({ userEmail: value || undefined }))
    })

  const handleRowClick = conversationId => {
    dispatch(setSelectedConversationId(conversationId))
    dispatch(fetchConversationThread(conversationId))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.grey[200]}`, flexShrink: 0 }}>
        <TextField
          label='Filter by email'
          value={emailFilter}
          onChange={handleEmailFilterChange}
          size='small'
          fullWidth
        />
      </Box>

      {conversationsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>User Email</TableCell>
                <TableCell>First Message</TableCell>
                <TableCell align='right'>Messages</TableCell>
                <TableCell align='right'>Feedback</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.map(conversation => (
                <TableRow
                  key={conversation.conversationId}
                  hover
                  onClick={() => handleRowClick(conversation.conversationId)}
                  selected={conversation.conversationId === selectedConversationId}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{conversation.userEmail}</TableCell>
                  <TableCell>
                    <Typography variant='body2' color='textSecondary'>
                      {truncateText(conversation.firstMessagePreview, 60)}
                    </Typography>
                  </TableCell>
                  <TableCell align='right'>{conversation.messageCount}</TableCell>
                  <TableCell align='right'>{conversation.feedbackCount}</TableCell>
                  <TableCell>
                    <Typography variant='caption' noWrap>
                      {formatDate(conversation.lastMessageAt)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {conversations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align='center'>
                    <Typography variant='body2' color='textSecondary'>
                      No conversations found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  )
}

export default ConversationTable
